import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import EmojiPicker from './EmojiPicker';

const ControlContainer = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 2rem;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  gap: 1rem;
  z-index: 9999;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    padding: 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const ControlButton = styled(motion.button)`
  background: ${props => props.active ? 
    'linear-gradient(135deg, #FF6B35, #F7931E)' : 
    'rgba(255, 255, 255, 0.1)'
  };
  border: 1px solid ${props => props.active ? 
    'transparent' : 
    'rgba(255, 255, 255, 0.2)'
  };
  color: ${props => props.disabled ? 
    'rgba(255, 255, 255, 0.3)' : 
    'white'
  };
  padding: 0.75rem;
  border-radius: 50%;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 1.2rem;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  position: relative;
  
  &:hover:not(:disabled) {
    background: ${props => props.active ? 
      'linear-gradient(135deg, #F7931E, #FFD23F)' : 
      'rgba(255, 255, 255, 0.2)'
    };
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 1.1rem;
  }
`;

const DangerButton = styled(ControlButton)`
  background: ${props => props.active ? 
    'linear-gradient(135deg, #ef4444, #dc2626)' : 
    'rgba(239, 68, 68, 0.2)'
  };
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #dc2626, #b91c1c);
  }
`;

const Separator = styled.div`
  width: 1px;
  height: 32px;
  background: rgba(255, 255, 255, 0.2);
  margin: 0 0.5rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const InfoBadge = styled(motion.div)`
  position: absolute;
  top: -8px;
  right: -8px;
  background: #ef4444;
  color: white;
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
`;

const Tooltip = styled(motion.div)`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
  white-space: nowrap;
  margin-bottom: 0.5rem;
  pointer-events: none;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
  }
`;

const StatusText = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.7rem;
  margin: 0 1rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const ConnectionStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatusDot = styled(motion.div)`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => {
    switch (props.status) {
      case 'excellent': return '#10b981';
      case 'good': return '#f59e0b';
      case 'poor': return '#ef4444';
      default: return '#6b7280';
    }
  }};
`;

function ControlBar({
  videoEnabled,
  audioEnabled,
  screenSharing,
  chatOpen,
  participantsOpen,
  whiteboardOpen,
  handRaised,
  isRecording,
  isHost,
  onToggleVideo,
  onToggleAudio,
  onToggleScreenShare,
  onToggleChat,
  onToggleParticipants,
  onToggleWhiteboard,
  onSendReaction,
  onToggleHand,
  onToggleRecording,
  onLeaveRoom,
  participantCount,
  unreadMessages = 0,
  connectionQuality = 'good'
}) {
  const [hoveredButton, setHoveredButton] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const handleReactionClick = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  const handleEmojiSelect = (emoji) => {
    onSendReaction(emoji);
    setShowEmojiPicker(false);
  };

  const mainButtons = [
    {
      id: 'audio',
      icon: audioEnabled ? '🎤' : '🔇',
      active: audioEnabled,
      onClick: onToggleAudio,
      tooltip: audioEnabled ? 'Disattiva microfono' : 'Attiva microfono',
      disabled: false
    },
    {
      id: 'video',
      icon: videoEnabled ? '📹' : '📷',
      active: videoEnabled,
      onClick: onToggleVideo,
      tooltip: videoEnabled ? 'Disattiva camera' : 'Attiva camera',
      disabled: false
    },
    {
      id: 'screen',
      icon: '🖥️',
      active: screenSharing,
      onClick: onToggleScreenShare,
      tooltip: screenSharing ? 'Ferma condivisione' : 'Condividi schermo',
      disabled: false
    }
  ];

  const secondaryButtons = [
    {
      id: 'whiteboard',
      icon: '🎨',
      active: whiteboardOpen,
      onClick: onToggleWhiteboard,
      tooltip: whiteboardOpen ? 'Chiudi lavagna' : 'Apri lavagna',
      disabled: false
    },
    {
      id: 'reaction',
      icon: '😊',
      active: showEmojiPicker,
      onClick: handleReactionClick,
      tooltip: 'Invia reaction',
      disabled: false
    },
    {
      id: 'hand',
      icon: '✋',
      active: handRaised,
      onClick: onToggleHand,
      tooltip: handRaised ? 'Abbassa mano' : 'Alza mano',
      disabled: false,
      badge: handRaised ? '!' : null
    },
    {
      id: 'participants',
      icon: '👥',
      active: participantsOpen,
      onClick: onToggleParticipants,
      tooltip: 'Partecipanti',
      badge: participantCount,
      disabled: false
    },
    {
      id: 'chat',
      icon: '💬',
      active: chatOpen,
      onClick: onToggleChat,
      tooltip: 'Chat',
      badge: unreadMessages > 0 ? unreadMessages : null,
      disabled: false
    }
  ];

  if (isHost) {
    secondaryButtons.push({
      id: 'recording',
      icon: isRecording ? '⏹️' : '⏺️',
      active: isRecording,
      onClick: onToggleRecording,
      tooltip: isRecording ? 'Ferma registrazione' : 'Avvia registrazione',
      disabled: false
    });
  }

  const getConnectionText = () => {
    switch (connectionQuality) {
      case 'excellent': return 'Ottima';
      case 'good': return 'Buona';
      case 'poor': return 'Scarsa';
      default: return 'Sconosciuta';
    }
  };

  return (
    <ControlContainer
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 100, damping: 20 }}
    >
      {/* Main Controls */}
      <ControlGroup>
        {mainButtons.map((button) => (
          <div 
            key={button.id} 
            style={{ position: 'relative' }}
            onMouseEnter={() => setHoveredButton(button.id)}
            onMouseLeave={() => setHoveredButton(null)}
          >
            <ControlButton
              active={button.active}
              disabled={button.disabled}
              onClick={button.onClick}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {button.icon}
            </ControlButton>

            <AnimatePresence>
              {hoveredButton === button.id && (
                <Tooltip
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  {button.tooltip}
                </Tooltip>
              )}
            </AnimatePresence>
          </div>
        ))}
      </ControlGroup>

      <Separator />

      {/* Status Info */}
      <StatusText
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <ConnectionStatus>
          <StatusDot
            status={connectionQuality}
            animate={{
              scale: [1, 1.2, 1]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <span>Connessione {getConnectionText()}</span>
        </ConnectionStatus>
        <div>🕐 {new Date().toLocaleTimeString('it-IT', { 
          hour: '2-digit', 
          minute: '2-digit' 
        })}</div>
      </StatusText>

      <Separator />

      {/* Secondary Controls */}
      <ControlGroup>
        {secondaryButtons.map((button) => (
          <div 
            key={button.id} 
            style={{ position: 'relative' }}
            onMouseEnter={() => setHoveredButton(button.id)}
            onMouseLeave={() => setHoveredButton(null)}
          >
            <ControlButton
              active={button.active}
              disabled={button.disabled}
              onClick={button.onClick}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {button.icon}
              {button.badge && (
                <InfoBadge
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {button.badge}
                </InfoBadge>
              )}
            </ControlButton>

            <AnimatePresence>
              {hoveredButton === button.id && (
                <Tooltip
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  {button.tooltip}
                </Tooltip>
              )}
            </AnimatePresence>
          </div>
        ))}

        <Separator />

        {/* Leave Room Button */}
        <div 
          style={{ position: 'relative' }}
          onMouseEnter={() => setHoveredButton('leave')}
          onMouseLeave={() => setHoveredButton(null)}
        >
          <DangerButton
            onClick={onLeaveRoom}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            📞
          </DangerButton>

          <AnimatePresence>
            {hoveredButton === 'leave' && (
              <Tooltip
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 5 }}
                transition={{ duration: 0.2 }}
              >
                Lascia la sala
              </Tooltip>
            )}
          </AnimatePresence>
        </div>
      </ControlGroup>

      {/* Emoji Picker */}
      <AnimatePresence>
        {showEmojiPicker && (
          <EmojiPicker 
            onEmojiSelect={handleEmojiSelect}
            onClose={() => setShowEmojiPicker(false)}
          />
        )}
      </AnimatePresence>
    </ControlContainer>
  );
}

export default ControlBar;
